// ABOUTME: Shows one audit log record: identifiers and old/new values per field, changed fields highlighted (HCP-112400).
import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  TableBase,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TextRegular,
  TextSemiBold,
} from '@hm/fundamental'
import React from 'react'

import type {AuditLogRecord} from '../auditLogTypes'

const formatValue = (value: unknown) => (value === undefined ? '—' : JSON.stringify(value, null, 2))

const valueClassName = 'm-0 whitespace-pre-wrap break-words text-sm'

type Props = {
  record: AuditLogRecord
  onClose: () => void
}

export const AuditLogDetailsDialog: React.FC<Props> = ({record, onClose}) => {
  const oldValues = record.oldValues ?? {}
  const newValues = record.newValues ?? {}
  const fields = Array.from(new Set([...Object.keys(oldValues), ...Object.keys(newValues)]))
  const changed = new Set(record.changedFields)

  const identifiers: Array<[string, string | null]> = [
    ['Entity id', record.entityId],
    ['Scope id', record.scopeId],
    ['Transaction id', record.transactionId],
    ['Trace id', record.traceId],
  ]

  return (
    <Modal open onClose={onClose} width="wide" data-cy="audit-log-details">
      <ModalTitle>
        {record.entityType} · {record.action}
        {record.reason ? ` (${record.reason})` : ''}
      </ModalTitle>
      <ModalContent>
        <TableBase className="w-full">
          <TableBody>
            {identifiers.map(([label, value]) => (
              <TableRow key={label}>
                <TableCell>
                  <TextSemiBold>{label}</TextSemiBold>
                </TableCell>
                <TableCell>
                  <TextRegular>{value ?? '—'}</TextRegular>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TableBase>
        {fields.length === 0 ? (
          <TextRegular className="mt-4 block">No values were recorded for this change.</TextRegular>
        ) : (
          <TableBase className="w-full mt-4">
            <TableHeader>
              <TableHeaderCell>Field</TableHeaderCell>
              <TableHeaderCell>Before</TableHeaderCell>
              <TableHeaderCell>After</TableHeaderCell>
            </TableHeader>
            <TableBody>
              {fields.map((name) => {
                const isChanged = changed.has(name)
                return (
                  <TableRow
                    key={name}
                    data-test-id={`audit-field-${name}`}
                    data-changed={String(isChanged)}
                    className={isChanged ? 'bg-warning_faded_10' : undefined}
                  >
                    <TableCell>
                      {isChanged ? <TextSemiBold>{name}</TextSemiBold> : <TextRegular>{name}</TextRegular>}
                    </TableCell>
                    <TableCell>
                      <pre className={valueClassName} data-test-id={`audit-old-${name}`}>
                        {formatValue(oldValues[name])}
                      </pre>
                    </TableCell>
                    <TableCell>
                      <pre className={valueClassName} data-test-id={`audit-new-${name}`}>
                        {formatValue(newValues[name])}
                      </pre>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </TableBase>
        )}
      </ModalContent>
      <ModalFooter justifyClass="justify-end">
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}
